import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { Subject, Subscription, ReplaySubject, Observable } from 'rxjs';

/**
 * Abstract service capable of memorizing T entities and
 * exposes the common operation on them:
 * - Add (one or more entity at time)
 * - Update (one or more entity at time)
 * - Get all entities stored
 * - Get all entities stored as observable
 * - Delete (one or more entity at time)
 * 
 * It is based on the the requirement that EACH entity
 * saved is unique (@see areEntitiesEqual method to determine
 * how)
 * 
 * This service is available on ALL PLATFORMs but it is not
 * high performance (consider SQL lite where available)
 * 
 * Based on @see https://ionicframework.com/docs/angular/storage 
 */
@Injectable()
export abstract class DictionaryStoreService<T> {
  private entities: T[];
  private savedEntitiesSubject: Subject<T[]>;
  private internalSubscription: Subscription;

  constructor(protected storage: Storage) {
    this.initializeObservable();
  }

  private initializeObservable() {
    this.savedEntitiesSubject = new ReplaySubject<T[]>(1);
    this.internalSubscription = this.savedEntitiesSubject.subscribe(list => this.entities = list);
    this.getEntities().then(startupEntitiesState => {
      if (!!startupEntitiesState) {
        this.savedEntitiesSubject.next(startupEntitiesState);
      } else {
        this.savedEntitiesSubject.next([]);
      }
    });
  }

  /**
   * Returns the observable of ALL the entities registered through this service
   */
  protected getSavedEntitiesObservable(): Observable<T[]> {
    return this.savedEntitiesSubject.asObservable();
  }

  /**
   * Returns the unique key used to refer to the entities saved on the local db
   */
  protected abstract getDbKey(): string;
  /**
   * TRUE when 2 entities are equal
   * @param a
   * @param b
   */
  protected abstract areEntitiesEqual(a: T, b: T): boolean;

  /**
   * Since all the entities are serialized as JSON on a browser dependenant way, at the construction
   * of the service you can specify wheter all entities will be saved on the db as string
   * (giving you the change to customize how the serialization process is handled via overwriting of toJSON 
   * method and customising the de-serialization process with this method) 
   * @param entityAsString 
   */
  protected abstract parseEntityFromString(entityAsString: string): T;

  /**
   * Returns the list of ALL the entities saved on the db,
   * an empty array if none is present
   */
  protected async getEntities(): Promise<T[]> {
    let retVal: T[] = [];

    try {
      const entitiesOnDb: any[] = (await this.storage.get(this.getDbKey()));
      if (!!entitiesOnDb) {
        retVal = entitiesOnDb;
      }
    } catch (error) {
      console.error(error);
    }
    return retVal;
  }

  /**
   * Add the given entity to the existing list of entities
   * If the given entity already is (@see areEntitiesEqual) in the collection, its' state
   * will be overwritten
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async addEntity(entity: T): Promise<boolean> {
    return this.updateEntity(entity);
  }

  /**
   * Add the given entities to the existing list of entities
   * If the given entities already are (@see areEntitiesEqual) in the collection, their state
   * will be overwritten
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async addEntities(entities: T[]): Promise<boolean> {
    return this.updateEntities(entities);
  }

  /**
   * Overwrites the db with the given entities
   * Side effect: publishing of the new entitites state
   * @param entitiesToSave 
   */
  private async writeEntities(entitiesToSave: T[]): Promise<boolean> {
    await this.storage.set(this.getDbKey(), entitiesToSave);
    this.savedEntitiesSubject.next(entitiesToSave);
    return true;
  }

  /**
   * Modifies the state of the given entity (by removing and readding it)
   * If the entity is not present they will be added
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async updateEntity(newEntityState: T): Promise<boolean> {
    return this.updateEntities([newEntityState]);
  }

  /**
   * Modifies the state of the given entities (by removing and readding them)
   * If the entities are not present they will be added
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async updateEntities(newEntitiesState: T[]): Promise<boolean> {
    let finalEntitiesState = await this.getEntities();
    finalEntitiesState = this.removeEntitiesFromCollection(finalEntitiesState, newEntitiesState);
    finalEntitiesState = finalEntitiesState.concat(newEntitiesState);
    return this.writeEntities(finalEntitiesState);
  }

  // Deletion
  protected async removeAllEntities(): Promise<boolean> {
    await this.storage.remove(this.getDbKey());
    this.savedEntitiesSubject.next([]);
    return true;
  }

  /**
   * Finds and delete the given entity
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async removeEntity(entityToRemove: T): Promise<boolean> {
    return await this.removeEntities([entityToRemove]);
  }

  /**
   * Finds and delete the given entities
   * Side effect: publishing of the new entitites state
   * @param entity 
   */
  protected async removeEntities(entitiesToRemove: T[]): Promise<boolean> {
    const entitiesActuallySaved = await this.getEntities();
    const filteredEntities = this.removeEntitiesFromCollection(entitiesActuallySaved, entitiesToRemove);
    return await this.writeEntities(filteredEntities);
  }

  private removeEntityFromCollection(collection: T[], entityToRemove: T): T[] {
    return collection.filter(existingEntity => !this.areEntitiesEqual(existingEntity, entityToRemove));
  }

  private removeEntitiesFromCollection(collection: T[], entitiesToRemove: T[]): T[] {
    let filteredEntities = collection;
    for (const entityToRemove of entitiesToRemove) {
      filteredEntities = this.removeEntityFromCollection(filteredEntities, entityToRemove);
    }
    return filteredEntities;
  }



}
